import React, { useEffect, useState } from 'react';
// import { useShallow } from 'zustand/react/shallow';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { ChatArea } from '@/components/chat/ChatArea';
import { InputArea } from '@/components/layout/InputArea';
import { ReconnectOverlay } from '@/components/overlay/ReconnectOverlay';
import { useAppStore } from '@/hooks/useAppStore';
import { useKeyboardViewport } from '@/hooks/useKeyboardViewport';
import GameLauncher from '@/components/chat/games/GameLauncher';
import WidgetWrapper from '@/components/chat/WidgetWrapper';
import { PresentationChatLayout } from '@/components/chat/PresentationChatLayout';
import { cn, toggleFullscreen } from '@/lib/utils';
import { MessagePopup } from '@/components/chat/MessagePopup';
import { FloatingMenu } from '@/components/layout/FloatingMenu';
import { SettingsDialog } from '@/components/dialogs/SettingsDialog';
import { MicrophoneDialog } from '@/components/dialogs/MicrophoneDialog';
import { LanguageSettingsDialog } from '@/components/dialogs/LanguageSettingsDialog';
import { useIsMobile } from "@/hooks/useIsMobile";
// import { create } from 'domain';
// import { useAudioPlayer } from '@/hooks/useAudioPlayer'; // Import player hook

export const TranslatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const routeRoomId = searchParams.get('r') // Get room ID from URL
  const navigate = useNavigate();

  // Select necessary state and actions from Zustand store
  const joinRoom = useAppStore((state) => state.joinRoom);
  const createRoom = useAppStore((state) => state.createRoom);
  const redirectToRoom = useAppStore((state) => state.redirectToRoom);
  const isConnected = useAppStore((state) => state.isConnected);
  const isConnecting = useAppStore((state) => state.isConnecting);
  const isReconnecting = useAppStore((state) => state.isReconnecting);
  const roomId = useAppStore((state) => state.roomId);

  const isMobile = useIsMobile();
  const isPresentationMode = useAppStore((state) => state.isPresentationMode);
  const setPresentationMode = useAppStore((state) => state.setPresentationMode);
  const isMessagePopupOpen = useAppStore((state) => state.isMessagePopupOpen);
  const clientConfig = useAppStore((state) => state.clientConfig);
  const { height: viewportHeight, isKeyboardVisible } = useKeyboardViewport();

  // Dialog states
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [microphoneDialogOpen, setMicrophoneDialogOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [fontSizeOpen, setFontSizeOpen] = useState(false);

  // Map clientConfig.fontSize to ChatArea textSize
  const fontSize = clientConfig?.fontSize || "normal";
  const fontSizeToTextSize: Record<string, "base" | "lg" | "xl" | "2xl" | "4xl"> = {
    normal: "base",
    large: "2xl",
    xlarge: "4xl",
  };
  const textSize = fontSizeToTextSize[fontSize] || "base";

  // const setPlayerQueueFunction = useAppStore((state) => state.setPlayerQueueFunction: (state as any).setPlayerQueueFunction);
  // const {
  //   connectWebSocket,
  //   setPlayerQueueFunction, // Action to register player queue function
  // } = useAppStore(useShallow(state => ({
  //     connectWebSocket: state.connectWebSocket,
  //     setPlayerQueueFunction: (state as any).setPlayerQueueFunction // Cast if not added to type yet
  // })));

  // Initialize Audio Player and register its queue function with the store
  // const { queueAudio } = useAudioPlayer();
  // useEffect(() => {
  //     if (setPlayerQueueFunction) {
  //         setPlayerQueueFunction(queueAudio);
  //     }
  //     // Optional cleanup: Deregister on unmount
  //     // return () => { if (setPlayerQueueFunction) setPlayerQueueFunction(() => {}); };
  // }, [queueAudio, setPlayerQueueFunction]);

  // Effect to handle connection based on URL parameter
  useEffect(() => {
    // if (initializedRoomRef.current && initializedRoomRef.current == routeRoomId) {
    //   // A bit of a hack for React 18 strict mode
    //   return;
    // }
    const { isConnected, isConnecting, isReconnecting } = useAppStore.getState();
    
    //Update the URL 
    if (isConnected && routeRoomId !== roomId) {
      navigate(`/translate?r=${roomId}`, { replace: true });
      return
    }

    if (isConnected || isConnecting || isReconnecting) {
      return;
    }

    if (routeRoomId) {
      // New room specified in URL always priority
      if (routeRoomId == 'new') {
        createRoom();
      } else {
        joinRoom(routeRoomId);
      }
    } else {
      redirectToRoom(roomId || 'new');
    }
  }, [isConnected, isConnecting, isReconnecting, routeRoomId, roomId, joinRoom, createRoom, redirectToRoom, useAppStore.getState().user]);

  useEffect(() => {
    if (isKeyboardVisible) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
      console.log('scrolling to the top');
    }
  }, [isKeyboardVisible]);


  return (
    <div className="flex flex-col h-[100dvh] w-full overflow-hidden">
      {/* Header - fixed height */}
      <Header />
      
      {/* Chat container - takes remaining space */}
      <div 
        className={cn("relative flex items-center justify-center p-0 chat-container flex-1 overflow-hidden",
        (isPresentationMode || isMobile) ? "" : "pb-6" // space for "Ai can make mistakes"
        )}
        style={{ 
          height: isKeyboardVisible ? `${viewportHeight - 85}px` : undefined,
        }}
      >
        {/* Floating Menu - hidden in presentation mode */}
        {!isPresentationMode && (
          <FloatingMenu
            onSettingsClick={() => setSettingsDialogOpen(true)}
            onAccountClick={() => {}}
            onPresentationClick={() => setPresentationMode(true)}
            onFullscreenClick={toggleFullscreen}
            onFontSizeClick={() => setFontSizeOpen(!fontSizeOpen)}
          />
        )}

        <div className={cn(`relative flex flex-col w-full overflow-hidden`,
          isPresentationMode ? "h-full" : "h-full max-w-xl rounded-lg"
        )}>
          {/* place where interesting things(WebGL/Canvas) can happen */}
          <WidgetWrapper />
          <GameLauncher />
          <div className="chat-body relative flex flex-col flex-1 min-h-0 overflow-hidden pb-[90px]">
            {isPresentationMode ? <PresentationChatLayout /> : <ChatArea textSize={textSize} />}
            <InputArea />
          </div>
          {/* Overlays */}
          {/* {isConnecting && <LoadingOverlay />} */}
          {isReconnecting && <ReconnectOverlay />}
          {/*
            Disabling ZoomOverlay.
           <ZoomOverlay /> 
           */}
        </div>
      </div>

      {/* Dialogs */}
      <SettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
      />
      <MicrophoneDialog
        open={microphoneDialogOpen}
        onOpenChange={setMicrophoneDialogOpen}
      />
      <LanguageSettingsDialog
        open={languageDialogOpen}
        onOpenChange={setLanguageDialogOpen}
      />

      {isMessagePopupOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          <MessagePopup />
        </div>
      )}
    </div>
  );
};

export default TranslatePage;
